import {
  useAssets,
  useAssetsIcons,
  useDepositWithdrawAssetsDictionary,
} from '@/shared/api';
import type { DepositWithdrawFiatType } from '@/shared/types';
import { useMemo } from 'react';
import type { CardIconConfig, CurrencyOption } from './types';

type CreateCardFormData = {
  isLoading: boolean;
  isError: boolean;
  currencies: CurrencyOption[];
  cardIconConfig: CardIconConfig;
};

export const useCreateCardFormData = (): CreateCardFormData => {
  const { assetsIconsData, isAssetsIconsLoading, assetsIconsError } =
    useAssetsIcons();

  const {
    depositWithdrawAssetsData,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
  } = useDepositWithdrawAssetsDictionary();

  const {
    data: assetsData,
    error: assetsError,
    isLoading: isAssetsLoading,
  } = useAssets({
    enabled: !!depositWithdrawAssetsData,
  });

  const isError =
    !!depositWithdrawAssetsError || !!assetsError || !!assetsIconsError;

  const isReady =
    !!depositWithdrawAssetsData &&
    !!assetsData &&
    !!assetsIconsData &&
    !depositWithdrawAssetsLoading &&
    !isAssetsLoading &&
    !isAssetsIconsLoading;

  const isLoading = !isReady && !isError;

  const currencies = useMemo(() => {
    if (!isReady) return [];

    const fiatAssets = depositWithdrawAssetsData.value.withdraw.fiat ?? [];

    const key = 'card' as keyof DepositWithdrawFiatType;

    const currencies = fiatAssets.find(item => item[key])?.[key];

    if (!currencies) return [];

    return currencies.map(currency => ({
      value: currency.code,
      code: currency.code,
      name:
        assetsData.value.find(asset => asset.code === currency.code)?.name ||
        '',
      icon: assetsIconsData.value?.[currency.code] || '',
    }));
  }, [isReady]);

  const cardIconConfig = useMemo(() => {
    if (!isReady) return {};

    const paymentSystems =
      depositWithdrawAssetsData?.value?.withdraw?.paymentSystem;
    if (!Array.isArray(paymentSystems)) return {};

    const cards = paymentSystems.find(item => item.cards)?.cards;
    if (!Array.isArray(cards)) return {};

    const config: CardIconConfig = {};

    cards.forEach(card => {
      if (!card.icon) return;

      card.startNum?.forEach(num => {
        config[num] = card.icon;
      });
    });

    return config;
  }, [isReady]);

  return {
    isLoading,
    isError,
    currencies,
    cardIconConfig,
  };
};
